import { formatCpfCnpj } from '@/shared/lib/format';

/**
 * Modelos de resposta ficticia da consulta basica de CNPJ da Vigidata
 * (`analise-credito-cnpj`), para o simulador (ADR 0083).
 *
 * A forma e a documentada do fornecedor (`{status, response}`, com os campos
 * `CreditScore D00`, `Restrictions`, `ActiveDebits` e `Partners`). Os dados sao todos
 * inventados: empresa, socia, documentos, valores e datas nao existem. Nenhum valor
 * veio de resposta real. O documento consultado e o que a pessoa informou na tela.
 *
 * Os numeros com casa decimal tem poucos digitos de proposito: o texto gerado chega
 * ao servidor como foi escrito, sem conversao de ponto flutuante.
 */

export type ModeloVigidata = 'regular' | 'score_baixo' | 'restricoes_e_divida';

export const modelosVigidata: {
  chave: ModeloVigidata;
  rotulo: string;
  descricao: string;
}[] = [
  {
    chave: 'regular',
    rotulo: 'Regular',
    descricao: 'Empresa ativa, score alto, sem restricao e sem divida ativa.',
  },
  {
    chave: 'score_baixo',
    rotulo: 'Score baixo',
    descricao: 'Empresa ativa, sem restricao, com score D00 420.',
  },
  {
    chave: 'restricoes_e_divida',
    rotulo: 'Com restricoes e divida ativa',
    descricao:
      'Indicador restritivo, uma restricao de area embargada e duas dividas ativas.',
  },
];

// Instantes fixos, em milissegundos UTC, como o fornecedor manda.
const fundacao = Date.UTC(2012, 2, 15);
const entradaSocia = Date.UTC(2012, 2, 15);
const publicacao = Date.UTC(2025, 5, 10);
const inscricao = Date.UTC(2024, 8, 2);
const criacao = Date.UTC(2026, 9, 20);

function socia() {
  return {
    _api_c2_EntryDate: entradaSocia,
    _api_c2_Quote: 100.0,
    _api_c2_Role: 49,
    _api_c2_Name: 'SOCIA FICTICIA DO SIMULADOR',
    _api_c2_Gender: 2,
    // CPF de exemplo, valido pelo digito e de ninguem.
    _api_c2_Document: '52998224725',
    _api_c2_DocumentType: 1,
    _api_c2_DocumentFormatted: '529.982.247-25',
    _api_c2_Ranking: 1,
    _api_c2_Status: 1,
    body_raw_text: null,
  };
}

function restricaoAreaEmbargada() {
  return {
    _api_c2_TypeId: 3,
    _api_c2_Name: 'RestricoesAreasEmbargadas',
    _api_c2_Description: 'Area embargada ficticia, para teste do simulador',
    _api_c2_ProcessNumber: '00000.000000/2025-00',
    _api_c2_PublishDate: publicacao,
    _api_c2_Ranking: 1,
    _api_c2_CreateDate: criacao,
    _api_c2_Status: 1,
    body_raw_text: null,
  };
}

function dividaAtiva(categoria: number, nome: string, valor: number, numero: string) {
  return {
    _api_c2_CategoryId: categoria,
    _api_c2_CategoryName: nome,
    _api_c2_Description: `Divida ficticia ${nome.toLowerCase()}`,
    _api_c2_EntryNumber: numero,
    _api_c2_EntryDate: inscricao,
    _api_c2_EntryValue: valor,
    _api_c2_Ranking: 1,
    _api_c2_CreateDate: criacao,
    _api_c2_Status: 1,
    body_raw_text: null,
  };
}

/** Texto do corpo ficticio, pronto para colar e editar. */
export function modeloVigidata(tipo: ModeloVigidata, documento: string): string {
  const digitos = documento.replace(/\D/g, '');
  const score =
    tipo === 'score_baixo' ? 420 : tipo === 'restricoes_e_divida' ? 610 : 820;
  const restritivo = tipo === 'restricoes_e_divida';
  const corpo = {
    status: 'success',
    response: {
      RazaoSocial: 'EMPRESA FICTICIA DO SIMULADOR LTDA',
      Name: 'EMPRESA FICTICIA DO SIMULADOR LTDA',
      Document: digitos,
      DocumentFormatted: formatCpfCnpj(digitos),
      Email: 'contato@empresa-ficticia.test',
      Address: 'Rua Ficticia, 100, Cidade Exemplo',
      BithDate: fundacao,
      CapitalSocial: 500000,
      FaturamentoPresumido: 2500000.5,
      PresumidoGrupo: 2500000.5,
      Porte: 'EPP',
      SituacaoFiscal: 'Ativa',
      TipoEmpresa: 11,
      Tributacao: 2,
      TotalFuncionarios: 25,
      // 4 e documento valido; 128, restritivo (documentacao do fornecedor).
      Flaglists: restritivo ? [4, 128] : [4],
      'CreditScore D00': score,
      'CreditScore D30': score + 5,
      'CreditScore D60': score + 10,
      CodigoCnaePrincipal: 4120400,
      DescCnaePrinc: 'Construcao de edificios',
      Restrictions: restritivo ? [restricaoAreaEmbargada()] : [],
      ActiveDebits: restritivo
        ? [
            dividaAtiva(1, 'PREVIDENCIARIO', 15234.56, '00.0.00.000000-01'),
            dividaAtiva(2, 'NAO PREVIDENCIARIO', 8750.1, '00.0.00.000000-02'),
          ]
        : [],
      Partners: [socia()],
    },
  };
  return JSON.stringify(corpo, null, 2);
}
